// /home/heliowallet/frontend/components/wallet/BalanceCard.tsx
'use client';

import { useBalance, useAccount } from 'wagmi';
import { useWallet } from '@/hooks/useWallet';
import { useSelectedRpc } from '@/hooks/useSelectedRpc';
import { useRpcPatcher } from '@/hooks/useRpcPatcher';
import {
  ArrowPathIcon,
  WalletIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { useEffect } from 'react';

interface BalanceCardProps {
  chainId: number | undefined;
  isLoadingBalance: boolean;
  onReloadBalance: () => void;
  tokenCount: number;
}

export default function BalanceCard({
  chainId,
  isLoadingBalance,
  onReloadBalance,
  tokenCount
}: BalanceCardProps) {
  const { address } = useAccount();
  
  // Use selected RPC
  const { publicClient: customPublicClient, selectedRpc } = useSelectedRpc(chainId);
  
  // Patch wagmi transport when RPC changes
  useRpcPatcher(chainId, selectedRpc || undefined);
  
  // Use balance - Wagmi should use the selected RPC via useRpcPatcher
  const { data: balance, refetch: refetchBalance } = useBalance({ address });
  
  const { nativeTokenPrice, tokens, tokenPrices, chainInfo } = useWallet();

  console.log(`💰 [BalanceCard] Selected RPC: ${selectedRpc}`);
  console.log(`💰 [BalanceCard] Balance: ${balance?.formatted} ${balance?.symbol}`);

  // Force refetch balance when chain changes
  useEffect(() => {
    if (address && chainId) {
      console.log(`🔄 [BalanceCard] Chain changed to ${chainId}, refetching balance...`);
      refetchBalance();
    }
  }, [chainId, address, refetchBalance]);

  const nativeBalance = parseFloat(balance?.formatted || '0');
  const nativeUsdValue = nativeTokenPrice ? nativeBalance * nativeTokenPrice : 0;

  // Calculate total USD value including tokens
  const totalUsdValue = (() => {
    let total = nativeUsdValue;
    
    // Add token values
    if (tokens && tokenPrices) {
      tokens.forEach(token => {
        const price = tokenPrices[token.address?.toLowerCase()];
        if (price && token.balance) {
          total += parseFloat(token.balance) * price;
        }
      });
    }
    
    return total;
  })();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg">
              <WalletIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Account Balance
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Portfolio Value
              </p>
            </div>
          </div>
          <button
            onClick={onReloadBalance}
            disabled={isLoadingBalance}
            className="p-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
            title="Refresh balance"
          >
            <ArrowPathIcon className={`h-5 w-5 text-gray-600 dark:text-gray-400 ${isLoadingBalance ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Balance Display */}
      <div className="p-6 space-y-4 flex-1">
        {/* Native Token Balance */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            {chainInfo?.icon ? (
              <img
                src={chainInfo.icon}
                alt={chainInfo.name}
                className="h-5 w-5 rounded-full"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="h-5 w-5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {balance?.symbol || chainInfo?.nativeCurrency?.symbol || 'ETH'}
            </p>
          </div>
          <div className="flex items-baseline gap-3">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {nativeBalance.toFixed(6)}
            </p>
            <span className="text-lg text-gray-600 dark:text-gray-400">
              {balance?.symbol || chainInfo?.nativeCurrency?.symbol || 'ETH'}
            </span>
          </div>
        </div>

        {/* USD Value */}
        <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CurrencyDollarIcon className="h-4 w-4 text-gray-500" />
                <p className="text-sm text-gray-600 dark:text-gray-400">USD Value</p>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                ${totalUsdValue.toFixed(2)}
              </p>
              {nativeTokenPrice && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  @ ${nativeTokenPrice.toFixed(2)} per {balance?.symbol || chainInfo?.nativeCurrency?.symbol || 'ETH'}
                </p>
              )}
            </div>
            
            {/* Token Count */}
            <div className="text-right">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tokens</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {tokenCount}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {tokenCount === 0 ? '(0)' : `(${tokenCount})`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Value</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              ${totalUsdValue.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}